import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  UploadOutlined,
  CloseOutlined,
  DeleteOutlined,
  PictureOutlined,
  CloudUploadOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useEdit } from "../../context/EditContext";
import { apiFetch } from "../../../core/utils/api";
import InfoIcon from "../InfoIcon";
import { typography, textColors, surfaces, borders, interactive } from "../../../design-system/tokens";
import { NetworkErrorState } from "../../../design-system/components/layouts";
import SubscriptionPlansModal from "../../../subscription/SubscriptionPlansModal";

export const ImageField = ({ label, help, value, onChange, infoText }) => {
  const { websiteId } = useEdit();
  const [isLoading, setIsLoading] = useState(false);
  const [s3Images, setS3Images] = useState([]);
  const [publicImages, setPublicImages] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [deletingImages, setDeletingImages] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("user");
  const containerRef = useRef(null);
  const [columns, setColumns] = useState(4);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  // Download is a premium feature: only Plus/Pro plans or an active access grant.
  // null = unknown (not yet resolved), true/false once resolved.
  const [downloadAccess, setDownloadAccess] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const MIN_IMAGE_WIDTH = 100;
  const GAP_PX = 8;

  // Load user images when component mounts
  useEffect(() => {
    loadUserImages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set initial selected image based on current value across both sources
  useEffect(() => {
    if (!value) return;
    const findIn = (arr) => arr.find((img) => img.url === value);
    const userMatch = findIn(s3Images);
    const publicMatch = findIn(publicImages);
    const match = userMatch || publicMatch;
    if (match) {
      setSelectedImageId(match.key || match.url);
      if (publicMatch) setActiveTab("public");
    }
  }, [value, s3Images, publicImages]);

  // Responsive columns
  useEffect(() => {
    if (!containerRef.current) return;
    const calcColumns = (width) => {
      if (!width || width <= 0) return 2;
      const perImageWithGap = MIN_IMAGE_WIDTH + GAP_PX;
      const raw = Math.floor((width + GAP_PX) / perImageWithGap);
      return Math.max(2, Math.min(6, raw));
    };
    const update = () => {
      const width = containerRef.current?.getBoundingClientRect().width || 0;
      const next = calcColumns(width);
      setColumns((prev) => (prev !== next ? next : prev));
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserImages = async () => {
    setIsLoading(true);
    setUploadError("");
    try {
      const s3Response = await apiFetch(`/api/s3-images/?include_public=true`, {
        credentials: "include",
      });
      if (s3Response.ok) {
        const s3Data = await s3Response.json();
        setS3Images(s3Data.images || []);
        setPublicImages(s3Data.public_images || []);
      } else if (s3Response.status === 401) {
        setUploadError("Please log in to access your images");
      } else {
        setUploadError("Failed to load images. S3 storage may not be configured.");
      }
    } catch (error) {
      console.error("Error loading S3 images:", error);
      setUploadError("Network error while loading images");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!websiteId) {
      setUploadError("Website ID not available");
      return;
    }
    setIsLoading(true);
    setUploadError("");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("website_id", websiteId);
    try {
      const response = await apiFetch(`/api/upload-image/`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        await loadUserImages();
        if (data.image) {
          const imageUrl = data.image.url;
          onChange(imageUrl);
          setSelectedImageId(data.image.key || imageUrl);
          setActiveTab("user");
        }
      } else if (response.status === 401) {
        setUploadError("Please log in to upload images");
      } else {
        const errorData = await response.json();
        setUploadError(errorData.error || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setUploadError("Network error during upload");
    } finally {
      setIsLoading(false);
    }
  };

  const validateAndUpload = useCallback(
    (file) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setUploadError("Please select an image file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadError("File size must be less than 10 MB");
        return;
      }
      handleImageUpload(file);
    },
    [websiteId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleFileSelect = (event) => {
    validateAndUpload(event.target.files[0]);
  };

  const handleImageSelect = (image) => {
    const imageId = image.key || image.url;
    setSelectedImageId(imageId);
    onChange(image.url);
  };

  const handleDeleteClick = (image, event) => {
    event.stopPropagation();
    setImageToDelete(image);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;
    const imageKey = imageToDelete.key || imageToDelete.url;
    setDeletingImages((prev) => new Set(prev).add(imageKey));
    try {
      const response = await apiFetch(`/api/delete-s3-image/`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ s3_key: imageToDelete.key }),
        credentials: "include",
      });
      if (response.ok) {
        setS3Images((prev) => prev.filter((img) => (img.key || img.url) !== imageKey));
        if (selectedImageId === imageKey) {
          setSelectedImageId(null);
          onChange("");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setUploadError(errorData.error || "Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      setUploadError("Network error while deleting image");
    } finally {
      setDeletingImages((prev) => {
        const s = new Set(prev);
        s.delete(imageKey);
        return s;
      });
      setShowDeleteConfirm(false);
      setImageToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };

  const handleRemoveImage = () => {
    setSelectedImageId(null);
    onChange("");
  };

  // Resolve whether the current user may download images (Plus/Pro or access grant).
  // Mirrors the publish gating: on a failed check we stay lenient and allow the action.
  const resolveDownloadAccess = useCallback(async () => {
    try {
      const res = await apiFetch("/api/subscription/current/", { credentials: "include" });
      if (!res.ok) {
        setDownloadAccess(true);
        return true;
      }
      const data = await res.json().catch(() => ({}));
      const isPaidPlan = (data?.plan || "free") !== "free";
      const hasAccessGrant = Boolean(data?.access_grant?.is_active);
      const allowed = isPaidPlan || hasAccessGrant;
      setDownloadAccess(allowed);
      return allowed;
    } catch (_) {
      setDownloadAccess(true);
      return true;
    }
  }, []);

  // Pre-resolve access on mount so the gate decision is instant on click.
  useEffect(() => {
    resolveDownloadAccess();
  }, [resolveDownloadAccess]);

  const handleDownloadImage = async (event) => {
    event?.stopPropagation();
    const url = value || "";
    if (!url) return;

    // Gate by plan: only Plus/Pro or active access grant may download. Everyone else
    // gets the same upgrade flow as the "publish website" action.
    const allowed = downloadAccess === null ? await resolveDownloadAccess() : downloadAccess;
    if (!allowed) {
      setUpgradeModalOpen(true);
      return;
    }

    const deriveFilename = () => {
      try {
        const pathname = new URL(url, window.location.href).pathname;
        const last = pathname.split("/").pop();
        return last ? decodeURIComponent(last) : "image";
      } catch (_) {
        const last = url.split("?")[0].split("/").pop();
        return last || "image";
      }
    };

    try {
      const response = await fetch(url, { mode: "cors", credentials: "omit" });
      if (!response.ok) throw new Error("Failed to fetch image");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = deriveFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback for cross-origin / opaque responses: open in a new tab so the
      // user can save it manually.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // ── Drag & drop handlers ──
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files?.length) {
      validateAndUpload(e.dataTransfer.files[0]);
    }
  };

  const currentImageUrl = value || "";
  const isNetworkError =
    typeof uploadError === "string" && uploadError.toLowerCase().includes("network error");

  // ── Render helpers ──
  const renderImageGrid = (images, allowDelete) => (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {images.map((image) => {
        const imageKey = image.key || image.url;
        const isSelected = selectedImageId === imageKey;
        const isDeleting = deletingImages.has(imageKey);
        return (
          <div
            key={imageKey}
            className={`if-thumb group ${isDeleting ? "opacity-40 pointer-events-none" : ""}`}
            onClick={() => !isDeleting && handleImageSelect(image)}
          >
            <div
              className={`if-thumb-inner ${
                isSelected ? "if-thumb-selected" : "if-thumb-idle"
              }`}
            >
              <img
                src={image.url}
                alt={image.is_public ? "Public image" : "User image"}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>

            {/* Delete badge – user images only, not selected */}
            {allowDelete && !isSelected && (
              <button
                type="button"
                onClick={(e) => handleDeleteClick(image, e)}
                disabled={isDeleting}
                className="if-delete-badge"
                title="Delete image"
              >
                <CloseOutlined style={{ fontSize: 10 }} />
              </button>
            )}

            {/* Unselect badge – selected image */}
            {isSelected && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                className="if-unselect-badge"
                title="Unselect image"
              >
                <CloseOutlined style={{ fontSize: 10 }} />
              </button>
            )}

            {/* Deleting overlay */}
            {isDeleting && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 dark:bg-black/70">
                <span className="text-white text-[10px] font-medium">Deleting…</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="field-wrapper" ref={containerRef}>
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? (
            <span className="ml-2 align-middle">
              <InfoIcon text={infoText} />
            </span>
          ) : null}
        </label>
      )}

      <div className={`if-root border ${borders.default} ${surfaces.primary} ${textColors.secondary} rounded-xl overflow-hidden`}>

        {/* ── Selected image preview ── */}
        {currentImageUrl && (
          <div className={`if-selected ${surfaces.secondary} border-b ${borders.default}`}>
            <div className="if-selected-head">
              <span className={`if-selected-label ${textColors.primary}`}>Selected image</span>
              <button
                type="button"
                onClick={handleRemoveImage}
                className={`if-remove-btn ${textColors.muted}`}
                title="Remove selection"
              >
                <CloseOutlined style={{ fontSize: 12 }} />
                <span>Remove</span>
              </button>
            </div>
            <div className="if-selected-preview">
              <div className="if-selected-frame">
                <img
                  src={currentImageUrl}
                  alt="Selected"
                  className="if-selected-img"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <button
                  type="button"
                  onClick={handleDownloadImage}
                  className="if-download-badge"
                  title="Download image"
                  aria-label="Download image"
                >
                  <DownloadOutlined style={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Drop zone + upload ── */}
        <div
          className={`if-dropzone ${isDragging ? "if-dropzone-active" : ""}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !isLoading && fileInputRef.current?.click()}
        >
          <CloudUploadOutlined className="if-dropzone-icon" />
          <span className="if-dropzone-text">
            {isLoading ? "Uploading…" : isDragging ? "Drop image here" : "Drag & drop or click to upload"}
          </span>
          <span className="if-dropzone-hint">JPG, PNG, GIF, WebP — max 10 MB</span>
        </div>

        {/* ── Error ── */}
        {uploadError && !isNetworkError && (
          <div className="if-error">
            <ExclamationCircleOutlined style={{ fontSize: 13 }} />
            <span>{uploadError}</span>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className={`if-tabs border-t ${borders.default}`}>
          <button
            type="button"
            className={`if-tab ${activeTab === "user" ? "if-tab-active" : ""}`}
            onClick={() => setActiveTab("user")}
          >
            Your images
            <span className="if-tab-count">{s3Images.length}</span>
          </button>
          <button
            type="button"
            className={`if-tab ${activeTab === "public" ? "if-tab-active" : ""}`}
            onClick={() => setActiveTab("public")}
          >
            Public images
            <span className="if-tab-count">{publicImages.length}</span>
          </button>
        </div>

        {/* ── Gallery ── */}
        <div className="if-gallery">
          {isNetworkError ? (
            <NetworkErrorState
              title="Network error"
              description="We couldn't load images right now. Please check your connection and try again."
              onRetry={loadUserImages}
              className="py-10"
            />
          ) : isLoading && s3Images.length === 0 && publicImages.length === 0 ? (
            /* Loading skeleton */
            <div className="if-skeleton-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {Array.from({ length: columns * 2 }).map((_, i) => (
                <div key={i} className="if-skeleton-item" />
              ))}
            </div>
          ) : activeTab === "user" ? (
            s3Images.length === 0 ? (
              <div className="if-empty">
                <PictureOutlined className="if-empty-icon" />
                <span>No images yet</span>
                <span className="if-empty-sub">Upload your first image above</span>
              </div>
            ) : (
              renderImageGrid(s3Images, true)
            )
          ) : publicImages.length === 0 ? (
            <div className="if-empty">
              <PictureOutlined className="if-empty-icon" />
              <span>No public images available</span>
            </div>
          ) : (
            renderImageGrid(publicImages, false)
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        aria-label="Upload image"
        className="sr-only"
      />

      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}

      {/* ── Upgrade gate for image download (Plus/Pro or access grant) ── */}
      <SubscriptionPlansModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        notice={
          <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <div className={`${typography.h5} ${textColors.primary}`}>Upgrade required to download</div>
            <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
              Downloading images is available on the{" "}
              <span className={`${textColors.primary} font-medium`}>Plus</span> and{" "}
              <span className={`${textColors.primary} font-medium`}>Pro</span> plans.
            </div>
          </div>
        }
      />

      {/* ── Delete confirmation modal ── */}
      {showDeleteConfirm && imageToDelete && (
        <div className="if-modal-overlay" onClick={handleDeleteCancel}>
          <div className={`if-modal ${surfaces.primary} ${textColors.secondary}`} onClick={(e) => e.stopPropagation()}>
            <div className="if-modal-icon-wrap">
              <DeleteOutlined className="if-modal-icon" />
            </div>
            <h3 className={`if-modal-title ${textColors.primary}`}>Delete this image?</h3>
            <p className={`if-modal-desc ${textColors.secondary}`}>
              This action cannot be undone. The image will be permanently removed.
            </p>
            <div className="if-modal-actions">
              <button type="button" onClick={handleDeleteCancel} className="if-modal-btn if-modal-cancel">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteConfirm} className="if-modal-btn if-modal-delete">
                <DeleteOutlined style={{ fontSize: 13 }} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Scoped styles ── */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Root */
          .if-root {
            display: flex;
            flex-direction: column;
          }

          /* ── Selected preview ── */
          .if-selected {
            padding: 10px 12px 14px;
          }
          .if-selected-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 10px;
          }
          .if-selected-label {
            font-size: 13px;
            font-weight: 600;
            flex: 1;
            min-width: 0;
          }
          .if-selected-preview {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .if-selected-frame {
            position: relative;
            display: inline-block;
            max-width: 100%;
            line-height: 0;
          }
          .if-selected-img {
            display: block;
            max-width: 100%;
            max-height: 200px;
            border-radius: 10px;
            object-fit: contain;
            box-shadow: 0 2px 12px rgba(0,0,0,0.14);
          }
          .dark .if-selected-img {
            box-shadow: 0 2px 12px rgba(0,0,0,0.4);
          }
          .if-download-badge {
            position: absolute;
            top: 6px;
            right: 6px;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #fff;
            background: rgba(17,24,39,0.62);
            -webkit-backdrop-filter: blur(4px);
            backdrop-filter: blur(4px);
            box-shadow: 0 1px 4px rgba(0,0,0,0.3);
            transition: background 150ms, transform 150ms;
            z-index: 2;
          }
          .if-download-badge:hover {
            background: rgba(17,24,39,0.88);
            transform: scale(1.06);
          }
          .if-remove-btn {
            display: flex;
            align-items: center;
            gap: 4px;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 12px;
            padding: 4px 8px;
            border-radius: 6px;
            transition: background 150ms;
          }
          .if-remove-btn:hover {
            background: rgba(0,0,0,0.06);
          }
          .dark .if-remove-btn:hover {
            background: rgba(255,255,255,0.08);
          }

          /* ── Dropzone ── */
          .if-dropzone {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            padding: 18px 12px;
            margin: 10px;
            border-radius: 10px;
            border: 2px dashed rgba(0,0,0,0.15);
            cursor: pointer;
            transition: border-color 200ms, background 200ms;
            text-align: center;
          }
          .dark .if-dropzone {
            border-color: rgba(255,255,255,0.12);
          }
          .if-dropzone:hover {
            border-color: #2563eb;
            background: rgba(37,99,235,0.04);
          }
          .dark .if-dropzone:hover {
            background: rgba(96,165,250,0.06);
          }
          .if-dropzone-active {
            border-color: #2563eb !important;
            background: rgba(37,99,235,0.08) !important;
          }
          .dark .if-dropzone-active {
            border-color: #60a5fa !important;
            background: rgba(96,165,250,0.1) !important;
          }
          .if-dropzone-icon {
            font-size: 24px;
            color: #2563eb;
            opacity: 0.7;
          }
          .dark .if-dropzone-icon {
            color: #60a5fa;
          }
          .if-dropzone-text {
            font-size: 13px;
            font-weight: 500;
          }
          .if-dropzone-hint {
            font-size: 11px;
            opacity: 0.5;
          }

          /* ── Error ── */
          .if-error {
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0 10px 6px;
            padding: 6px 10px;
            border-radius: 8px;
            background: #fef2f2;
            color: #dc2626;
            font-size: 12px;
            font-weight: 500;
          }
          .dark .if-error {
            background: rgba(220,38,38,0.1);
            color: #fca5a5;
          }

          /* ── Tabs ── */
          .if-tabs {
            display: flex;
            gap: 2px;
            padding: 6px 10px;
          }
          .if-tab {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 5px 12px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
            background: transparent;
            color: inherit;
            white-space: nowrap;
            transition: background 150ms, color 150ms;
          }
          .if-tab:hover {
            background: rgba(0,0,0,0.05);
          }
          .dark .if-tab:hover {
            background: rgba(255,255,255,0.07);
          }
          .if-tab-active {
            background: #2563eb !important;
            color: #fff !important;
          }
          .if-tab-count {
            font-size: 10px;
            opacity: 0.7;
          }

          /* ── Gallery ── */
          .if-gallery {
            max-height: 300px;
            overflow-y: auto;
            padding: 10px;
          }

          /* ── Thumbnails ── */
          .if-thumb {
            position: relative;
            cursor: pointer;
            aspect-ratio: 1 / 1;
            transition: transform 100ms;
          }
          .if-thumb:hover {
            transform: translateY(-1px);
          }
          .if-thumb-inner {
            width: 100%;
            height: 100%;
            border-radius: 8px;
            overflow: hidden;
            border: 2px solid transparent;
            transition: border-color 150ms, box-shadow 150ms;
          }
          .if-thumb-idle:hover {
            border-color: rgba(37,99,235,0.35);
          }
          .if-thumb-selected {
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
          }
          .dark .if-thumb-selected {
            border-color: #60a5fa;
            box-shadow: 0 0 0 3px rgba(96,165,250,0.18);
          }

          /* ── Delete / unselect badges ── */
          .if-delete-badge,
          .if-unselect-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: opacity 200ms, transform 150ms;
            z-index: 2;
            box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          }
          .if-delete-badge {
            background: #ef4444;
            color: #fff;
            opacity: 0;
            transform: scale(0.8);
          }
          .if-thumb:hover .if-delete-badge {
            opacity: 1;
            transform: scale(1);
          }
          .if-unselect-badge {
            background: #374151;
            color: #fff;
          }
          .dark .if-unselect-badge {
            background: #4b5563;
          }
          .if-unselect-badge:hover {
            background: #1f2937;
          }
          .dark .if-unselect-badge:hover {
            background: #374151;
          }

          /* ── Empty state ── */
          .if-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 28px 12px;
            text-align: center;
            font-size: 13px;
            opacity: 0.55;
          }
          .if-empty-icon {
            font-size: 28px;
            margin-bottom: 2px;
          }
          .if-empty-sub {
            font-size: 11px;
          }

          /* ── Skeleton ── */
          .if-skeleton-grid {
            display: grid;
            gap: 8px;
          }
          .if-skeleton-item {
            aspect-ratio: 1 / 1;
            border-radius: 8px;
            background: linear-gradient(110deg, rgba(0,0,0,0.06) 30%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.06) 70%);
            background-size: 200% 100%;
            animation: if-shimmer 1.4s ease-in-out infinite;
          }
          .dark .if-skeleton-item {
            background: linear-gradient(110deg, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.06) 70%);
            background-size: 200% 100%;
          }
          @keyframes if-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          /* ── Modal ── */
          .if-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.45);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 50;
          }
          .dark .if-modal-overlay {
            background: rgba(0,0,0,0.65);
          }
          .if-modal {
            border-radius: 14px;
            padding: 28px 24px 20px;
            max-width: 340px;
            width: calc(100% - 32px);
            box-shadow: 0 20px 60px rgba(0,0,0,0.2);
            text-align: center;
          }
          .if-modal-icon-wrap {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: #fef2f2;
            margin-bottom: 14px;
          }
          .dark .if-modal-icon-wrap {
            background: rgba(220,38,38,0.12);
          }
          .if-modal-icon {
            font-size: 20px;
            color: #dc2626;
          }
          .dark .if-modal-icon {
            color: #f87171;
          }
          .if-modal-title {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 6px;
          }
          .if-modal-desc {
            font-size: 13px;
            margin: 0 0 20px;
            line-height: 1.5;
          }
          .if-modal-actions {
            display: flex;
            gap: 8px;
          }
          .if-modal-btn {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            padding: 8px 14px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: background 150ms;
          }
          .if-modal-cancel {
            background: rgba(0,0,0,0.06);
            color: inherit;
          }
          .dark .if-modal-cancel {
            background: rgba(255,255,255,0.08);
          }
          .if-modal-cancel:hover {
            background: rgba(0,0,0,0.1);
          }
          .dark .if-modal-cancel:hover {
            background: rgba(255,255,255,0.12);
          }
          .if-modal-delete {
            background: #dc2626;
            color: #fff;
          }
          .if-modal-delete:hover {
            background: #b91c1c;
          }
        `,
      }} />
    </div>
  );
};
